import { cn } from '@/lib/utils';
import { formatEur, pickSum } from '@/lib/cashFlow';
import type { CarriedForwardGroup } from '@progear/finance-core';
import { useCalendar } from './context';

/**
 * The collapsed carried-forward summary chip — one per direction — the calendar
 * folds overdue, UNPRIORITISED documents into on today's cell (replit.md module
 * 1.1 / F1). It shows how many documents are folded and their finance-core money
 * total (`pickSum` — a selection in the active money mode, never component maths;
 * L9) and, on click, drills into the register scoped to EXACTLY those ids — the
 * same scoped Ledger the table opens (L14). Styled distinctly from a single chip
 * (full border + stronger tint + hatch + the ⏱ glyph + a count badge) so it never
 * reads as one document.
 */
export function CarriedForwardChip({ kind, group }: { kind: 'income' | 'cost'; group: CarriedForwardGroup }) {
  const { mode, openCarriedForward } = useCalendar();
  const isIncome = kind === 'income';
  const amount = pickSum(group.total, mode);
  const count = group.ids.length;

  return (
    <button
      type="button"
      data-carried={kind}
      onClick={() => openCarriedForward(kind, group.ids)}
      title={`Ava üle tähtaja ${isIncome ? 'laekumised' : 'maksed'} registris (${count})`}
      className={cn(
        'pg-hatched flex items-center gap-1 rounded border border-l-2 px-1 py-0.5 text-[10px] font-semibold leading-tight transition-shadow hover:ring-1 hover:ring-ring',
        isIncome
          ? 'border-[hsl(var(--income))]/60 bg-[hsl(var(--income))]/20 text-[hsl(var(--income))]'
          : 'border-[hsl(var(--cost))]/60 bg-[hsl(var(--cost))]/20 text-[hsl(var(--cost))]',
      )}
    >
      <span aria-hidden className="shrink-0">
        ⏱
      </span>
      <span className="min-w-0 flex-1 truncate text-left text-foreground/90">
        Üle tähtaja · {isIncome ? 'laekumised' : 'maksed'}
      </span>
      <span className="shrink-0 rounded bg-foreground/15 px-1 text-[8px] font-medium tabular-nums text-foreground/70">
        {count}
      </span>
      <span className="shrink-0 tabular-nums">
        {isIncome ? '+' : '−'}
        {formatEur(amount)}
      </span>
    </button>
  );
}
